import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import AddBreakdownSheet from "../AddBreakdownSheet";

import ResourceRecoveryTableHeader, {
  ResourceRecoveryDetailsTableRow,
} from "./ResourceRecoveryTables";

import { SelectMulti } from "@/components/reusables/custom-form-fields/FormSelect";
import { Button } from "@/components/ui/button";
import * as Table from "@/components/ui/table";
import { useGetTippingRecordQuery } from "@/redux/api/reportsQuery";
import { useGetWasteTypeQuery } from "@/redux/api/wasteManagementTypeQuery";
import { TDailyReport } from "@/schemas/daily-report.schema";

const getFilteredData = (
  selectedTippingId:
    | {
        label: string;
        value: string;
      }[]
    | undefined,
  recordOptions: {
    data: {
      weighScaleTicketItems: TResourceRecoveryDetails[];
      id: string;
      ticketNumber: number;
    }[];
  },
): TResourceRecoveryDetails[] => {
  if (!selectedTippingId) {
    return [];
  }

  const idsToMatchValues = selectedTippingId?.map((item) => item.value);

  const filteredRecords = recordOptions?.data?.filter((single) =>
    idsToMatchValues.includes(single.id),
  );

  const tempAllWeighScaleTicketItems =
    filteredRecords &&
    filteredRecords.map((single) => {
      return single?.weighScaleTicketItems?.map((singleWeigh) => {
        return { ...singleWeigh, ticketNumber: single.ticketNumber };
      });
    });

  const tableData = tempAllWeighScaleTicketItems
    ? tempAllWeighScaleTicketItems.flat().map((ticket) => {
        return {
          ...ticket,
          breakDown: ticket?.breakDownData,
          tickedId: ticket?.id,
        };
      })
    : [];

  return tableData;
};

const ResourceRecovery = ({ id }: { id?: string }): JSX.Element => {
  const [selectedBreakdownId, setSelectedBreakdownId] = useState(0);
  const [isBreakDownSheetOpen, setIsBreakDownSheetOpen] = useState(false);
  const [finalLists, setFinalLists] = useState<TResourceRecoveryDetails[]>([]);
  const { control, watch, setValue, getValues } =
    useFormContext<TDailyReport>();
  const { projectId } = watch();
  const recordOptions = useGetTippingRecordQuery(
    { projectId, dailyReportId: id },
    {
      skip: !projectId,
    },
  );
  const { data: wastetypeDataResponse } = useGetWasteTypeQuery({});
  const wastetypeData = wastetypeDataResponse?.data;
  const formsTippingRecord = watch("resourceRecoveryDetails.tippingRecords");

  const {
    fields: allResourceRecoveryFields,
    prepend,
    remove,
    append,
    replace,
  } = useFieldArray({
    name: "resourceRecoveryDetails.manualData",
    control,
    keyName: "idx",
  });

  const handleSelectOnDelete = (tippingId: string): void => {
    const currentSelectedList = getValues(
      "resourceRecoveryDetails.tippingRecords",
    );
    //check if the selected tipping ids are in the list
    const isSelectedTippingInList = watch(
      "resourceRecoveryDetails.manualData",
    )?.some((field) => field?.tippingId === tippingId);
    if (!isSelectedTippingInList) {
      const newSelectedList = currentSelectedList?.filter(
        (item) => item.value !== tippingId,
      );
      setValue(`resourceRecoveryDetails.tippingRecords`, newSelectedList, {
        shouldDirty: true,
      });
    }
  };

  const handleItemsDelete = (
    index: number,
    id: string | undefined,
    tippingId?: string,
  ): void => {
    remove(index);
    if (id) {
      // to handle the deletion of the weighScaleTicketItems that are saved in the database
      const currentDeletedList = getValues("deleteWeighScaleTicketItems") || [];
      const newDeletedList = [...currentDeletedList, id];
      setValue(`deleteWeighScaleTicketItems`, newDeletedList, {
        shouldDirty: true,
      });
      //Also keep the deleted tipping ids in the list
      if (tippingId) {
        //Also keep the selected list in the delete list
        const currentDeletedUsedTicketList =
          getValues("resourceRecoveryDetails.deletedUsedTickets") || [];
        const newDeletedUsedTicketList = [
          ...currentDeletedUsedTicketList,
          tippingId,
        ];
        setValue(
          `resourceRecoveryDetails.deletedUsedTickets`,
          newDeletedUsedTicketList,
          {
            shouldDirty: true,
          },
        );
      }
    }
    // for multiselect handling
    if (tippingId) {
      handleSelectOnDelete(tippingId);
    }
  };

  // prepend the ticket to the load if there exist the data in the tickets
  useEffect(() => {
    const allResults = getFilteredData(formsTippingRecord, {
      data: (recordOptions?.data || []) as unknown as {
        weighScaleTicketItems: TResourceRecoveryDetails[];
        id: string;
        ticketNumber: number;
      }[],
    });

    setFinalLists(allResults);

    allResults?.forEach((singleTipping: TResourceRecoveryDetails) => {
      const doIdExist = allResourceRecoveryFields.some((field) => {
        return field?.tippingId === singleTipping?.weighScaleTicketId;
      });

      if (!doIdExist) {
        prepend({
          tippingId: singleTipping.weighScaleTicketId, //tipping is treated as weighScaleTicket
          ticketNumber: singleTipping.ticketNumber,
          loadType: {
            id: singleTipping.loadType.id,
            quantity: singleTipping.loadTypeQuantity,
          },
          wasteType: {
            id: singleTipping.wasteType.id,
            weight: singleTipping.wasteTypeWeight,
          },
          breakDown: singleTipping.breakDown
            ? {
                mixedWaste:
                  singleTipping?.breakDown?.mixedWaste?.map(
                    (singleMixedWaste) => {
                      return {
                        wasteTypeId: singleMixedWaste.wasteTypeId || "",
                        percentage: singleMixedWaste.percentage || 0,
                        wasteName: singleMixedWaste.wasteName || "",
                      };
                    },
                  ) ?? [],
                otherWaste:
                  singleTipping?.breakDown?.otherWaste?.map(
                    (singleOtherWaste) => {
                      return {
                        wasteTypeId: singleOtherWaste.wasteTypeId || "",
                        wasteName: singleOtherWaste.wasteName || "",
                      };
                    },
                  ) ?? [],
              }
            : {},
        });
      }
    });
  }, [formsTippingRecord]);

  useEffect(() => {
    const allKeys = formsTippingRecord?.map((single) => single.value);

    const tempLists = allResourceRecoveryFields?.filter((single) => {
      if (single?.tippingId) {
        if (allKeys?.includes(single?.tippingId)) {
          return single;
        }
      } else {
        return single;
      }

      return null;
    });

    replace(tempLists);
  }, [finalLists]);

  return (
    <div className='my-8'>
      <h2 className='mb-4 text-lg font-medium'>Resource Recovery Details</h2>
      <SelectMulti
        control={control}
        name={`resourceRecoveryDetails.tippingRecords`}
        placeholder='Select Tickets'
        className='w-[500px]'
        isRequired
        options={
          recordOptions?.data?.length
            ? recordOptions?.data?.map((record) => ({
                label: `Ticket - ${record.ticketNumber.toString()}`,
                value: record.id,
              }))
            : []
        }
        maxCount={2}
      />
      <section className='mt-4'>
        <div className='border rounded-sm  overflow-clip'>
          <Table.Table className=''>
            <ResourceRecoveryTableHeader />
            <Table.TableBody>
              {allResourceRecoveryFields?.map((field, index) => {
                return (
                  <ResourceRecoveryDetailsTableRow
                    key={field.idx}
                    field={field}
                    index={index}
                    setBreakDownIsOpen={setIsBreakDownSheetOpen}
                    setSelectedBreakdownId={setSelectedBreakdownId}
                    handleItemsDelete={handleItemsDelete}
                  />
                );
              })}
              <Table.TableRow className='!bg-background dark:bg-primary'>
                <Table.TableCell colSpan={11} className='text-left'>
                  <Button
                    className='mt-2 max-w-[200px]'
                    type='button'
                    onClick={() =>
                      append({
                        loadType: {
                          id: "",
                          quantity: 0,
                        },
                        wasteType: {
                          id: "",
                          weight: 0,
                        },
                        breakDown: {
                          mixedWaste: [
                            {
                              wasteTypeId: "",
                              percentage: 0,
                              wasteName: "",
                            },
                          ],
                          otherWaste: [
                            {
                              wasteTypeId: "",
                              wasteName: "",
                            },
                          ],
                        },
                      })
                    }
                    variant={"light"}
                  >
                    + Add New Row
                  </Button>
                </Table.TableCell>
              </Table.TableRow>
            </Table.TableBody>
          </Table.Table>
        </div>
      </section>
      <section>
        {isBreakDownSheetOpen && (
          <AddBreakdownSheet
            expanded={isBreakDownSheetOpen}
            defaultValues={
              allResourceRecoveryFields[selectedBreakdownId]
                ?.breakDown as TResourceRecoveryDetails["breakDown"]
            }
            wastetypeData={
              (wastetypeData as {
                name: string;
                recyclingPercentage: number;
                id: string;
                recoveryPathway: string;
              }[]) || []
            }
            selectedBreakdownId={selectedBreakdownId}
            toggleOpen={() => setIsBreakDownSheetOpen(!isBreakDownSheetOpen)}
          />
        )}
      </section>
    </div>
  );
};

export default ResourceRecovery;
